'use client';

import RailsApiResponse from '@apptypes/api/RailsApiResponse.ts';
import UserBalances from '@apptypes/api/user/Balances.ts';
import { PagesName } from '@constants';
import { useRoutePath } from '@hooks/index.ts';
import { useAuth } from '@hooks/useAuth.tsx';
import { setBalance, setBalances } from '@state/reducers/user.ts';
import { getApi } from '@utils/apiUtils.ts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

export function UseGetBalances<T = any>({
  interval = 5000,
  onData,
  socketConnected = false,
}: {
  interval?: number;
  onData?: (data: T) => void;
  socketConnected?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();

  const deposits = useRoutePath(PagesName.DepositPage, true);
  const withdraws = useRoutePath(PagesName.WithdrawalPage, true);
  const game = useRoutePath(PagesName.CasinoInnerPage, true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getApi<RailsApiResponse<UserBalances>>(
        '/restapi/v2/user/balances',
        { cache: 'no-cache' },
      );
      dispatch(setBalance(res.Data.withdrawable_balance));
      dispatch(setBalances(res.Data));
      onData?.(res.Data as T);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [dispatch, onData]);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    timerRef.current = setInterval(fetchData, interval);
  }, [fetchData, interval, stopPolling]);

  useEffect(() => {
    const onBalancePollingRoute =
      location.pathname === deposits ||
      location.pathname.startsWith(deposits) ||
      location.pathname === withdraws ||
      location.pathname.startsWith(withdraws) ||
      !!matchPath(location.pathname, {
        path: game,
        exact: false,
        strict: false,
      });
    const shouldPoll =
      user?.logged_in && !socketConnected && onBalancePollingRoute;

    if (!shouldPoll) {
      stopPolling();
      return;
    }

    void fetchData();
    startPolling();

    return () => stopPolling();
  }, [
    deposits,
    fetchData,
    game,
    location.pathname,
    startPolling,
    stopPolling,
    socketConnected,
    user?.logged_in,
    withdraws,
  ]);

  return {
    loading,
    error,
    refetch: fetchData,
    stopPolling,
    startPolling,
  };
}
